import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useSession } from '../../contexts/SessionContext';

const SUB_TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'partes', label: 'Partes de trabajo' },
];

function fmtFecha(f) {
  if (!f) return '—';
  return new Date(f).toLocaleDateString('es-ES');
}

function fmtEuro(v) {
  return Number(v || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export default function ProyectoSession({ sesion }) {
  const { cambiarSubTab } = useSession();
  const subTab = sesion.subTab || 'resumen';

  const [proyecto, setProyecto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [partes, setPartes] = useState([]);
  const [cargandoPartes, setCargandoPartes] = useState(false);

  const [rentabilidad, setRentabilidad] = useState(null);
  const [cargandoRent, setCargandoRent] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/proyectos/${sesion.entityId}`);
      setProyecto(data);
    } catch {
      setError('No se pudo cargar el proyecto.');
    } finally {
      setCargando(false);
    }
  }, [sesion.entityId]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (subTab !== 'partes') return;
    setCargandoPartes(true);
    api.get(`/partes-trabajo?proyectoId=${sesion.entityId}`)
      .then(({ data }) => setPartes(Array.isArray(data) ? data : data.docs || []))
      .catch(() => setPartes([]))
      .finally(() => setCargandoPartes(false));
  }, [subTab, sesion.entityId]);

  const verRentabilidad = async () => {
    setCargandoRent(true);
    try {
      const { data } = await api.get(`/proyectos/${sesion.entityId}/rentabilidad`);
      setRentabilidad(data);
    } catch {
      alert('No se pudo obtener la rentabilidad.');
    } finally {
      setCargandoRent(false);
    }
  };

  if (cargando) return <div className="session-content"><p className="empty">Cargando…</p></div>;
  if (error) return <div className="session-content"><div className="alert-error">{error}</div></div>;
  if (!proyecto) return null;

  return (
    <div>
      <div className="session-subtabs">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            className={`session-subtab ${subTab === t.id ? 'session-subtab--active' : ''}`}
            onClick={() => cambiarSubTab(sesion.id, t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'resumen' && (
        <div className="session-content">
          <div className="session-panels" style={{ padding: 0, marginBottom: 24 }}>
            <div className="session-panel">
              <div className="session-panel-title">Datos del proyecto</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <tbody>
                  {[
                    ['Título', proyecto.titulo],
                    ['Estado', proyecto.estado],
                    ['Tipo', proyecto.tipo],
                    ['Fecha inicio', fmtFecha(proyecto.fechaInicio)],
                    ['Fecha fin', fmtFecha(proyecto.fechaFin)],
                  ].map(([l, v]) => (
                    <tr key={l}>
                      <td style={{ color: 'var(--text-soft)', paddingBottom: 6, paddingRight: 12, fontWeight: 600, fontSize: 12 }}>{l}</td>
                      <td style={{ paddingBottom: 6 }}>{v || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {proyecto.notas && (
              <div className="session-panel">
                <div className="session-panel-title">Notas</div>
                <p style={{ fontSize: 14, color: 'var(--text)', margin: 0, whiteSpace: 'pre-wrap' }}>{proyecto.notas}</p>
              </div>
            )}
          </div>

          <button className="btn" onClick={verRentabilidad} disabled={cargandoRent}>
            {cargandoRent ? 'Cargando…' : 'Ver rentabilidad'}
          </button>

          {rentabilidad && (
            <div className="session-panels" style={{ marginTop: 16, padding: 0 }}>
              {Object.entries(rentabilidad).map(([k, v]) => (
                <div key={k} className="session-panel">
                  <div className="session-panel-title">{k}</div>
                  <div className="session-kpi" style={{ fontSize: 20 }}>
                    {typeof v === 'number' ? fmtEuro(v) : String(v)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'partes' && (
        <div className="session-content">
          {cargandoPartes ? <p className="empty">Cargando…</p> : partes.length === 0 ? (
            <p className="empty">Sin partes de trabajo.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr><th>Descripción</th><th>Fecha</th><th>Operario</th><th>Horas</th></tr>
                </thead>
                <tbody>
                  {partes.map(p => (
                    <tr key={p._id}>
                      <td>{p.descripcion || '—'}</td>
                      <td>{fmtFecha(p.fecha)}</td>
                      <td>{p.operario?.nombre || p.operario || '—'}</td>
                      <td>{p.horas ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
